import React, { useEffect, useState } from 'react'
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps'
import { Checkin, Feature } from '../../api/client'
import {
    extractCheckinLocationDetails,
    RenderedLocation,
} from '../LocationChips'
import { computeView } from '../CheckinLocationsMap'
import {
    LocationDetailValue,
    LocationDetailView,
} from './LocationDetailModal'
import { PIN_COLORS } from './pinStyle'

/**
 * One modal, two views: a wide interactive overview of every coord-bearing
 * pin on the checkin, and the detail-view-with-recent-checkins card for any
 * single pin. Tapping a pin on the overview swaps the content in place
 * (no second modal); the detail header carries a back arrow to return.
 *
 * This is the answer to "I want to look around the map, then drill in to
 * one place" without ever stacking modals. The chip click on the timeline
 * still uses the standalone `LocationDetailModal` — opening THIS modal in
 * detail mode would also work but offers no real upside, since chip click
 * has no overview to fall back to.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
const EXPANDED_MAP_ID = 'memgenie-checkin-locations-expanded'

interface CheckinExpandedMapModalProps {
    open: boolean
    checkin: Checkin
    collectionItems: Record<string, any>
    /** Page-level feature list, used to resolve the user-global Locations
     *  feature_id when `feature_details` is stale. Mirrors the chip and
     *  inline-map fix. */
    features?: Feature[]
    onClose: () => void
}

const CheckinExpandedMapModal: React.FC<CheckinExpandedMapModalProps> = ({
    open,
    checkin,
    collectionItems,
    features,
    onClose,
}) => {
    // Null = overview map; non-null = drilled into a single place. The
    // back arrow on the detail header clears this so the same modal goes
    // back to the overview.
    const [selectedDetail, setSelectedDetail] = useState<LocationDetailValue | null>(null)

    // Reset to overview every time the modal opens — otherwise the second
    // "Expand" press in a session would land in whatever detail view the
    // user last drilled into, which is surprising.
    useEffect(() => {
        if (open) setSelectedDetail(null)
    }, [open])

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            // Esc backs out one level: detail → overview, overview → close.
            if (selectedDetail) setSelectedDetail(null)
            else onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, selectedDetail, onClose])

    if (!open) return null

    // Backdrop click backs out one level — same as Esc — so the user
    // gets consistent "dismiss this layer" semantics regardless of input.
    const onBackdrop = () => {
        if (selectedDetail) setSelectedDetail(null)
        else onClose()
    }

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] p-4"
            onClick={(e) => {
                // Only treat as a backdrop dismiss when the click actually
                // started on the backdrop. Without this guard, a mousedown
                // inside the card that ends on the backdrop (text-selection
                // drag, etc.) would close the modal. We intentionally do
                // NOT use onMouseDown stopPropagation on the wrapper —
                // AdvancedMarker's click detection involves pointerdown/up
                // tracking that breaks if we kill the native event early.
                if (e.target === e.currentTarget) onBackdrop()
            }}
        >
            {selectedDetail ? (
                <LocationDetailView
                    value={selectedDetail}
                    onBack={() => setSelectedDetail(null)}
                    onClose={onClose}
                />
            ) : (
                <OverviewCard
                    checkin={checkin}
                    collectionItems={collectionItems}
                    features={features}
                    onPinSelect={setSelectedDetail}
                    onClose={onClose}
                />
            )}
        </div>
    )
}

interface OverviewCardProps {
    checkin: Checkin
    collectionItems: Record<string, any>
    features?: Feature[]
    onPinSelect: (value: LocationDetailValue) => void
    onClose: () => void
}

const OverviewCard: React.FC<OverviewCardProps> = ({
    checkin,
    collectionItems,
    features,
    onPinSelect,
    onClose,
}) => {
    const rendered = extractCheckinLocationDetails(checkin, collectionItems, features)
    const pins: Array<RenderedLocation & { lat: number; lng: number }> = []
    for (const r of rendered) {
        const lat = r.detailValue?.lat
        const lng = r.detailValue?.lng
        if (typeof lat === 'number' && typeof lng === 'number') {
            pins.push({ ...r, lat, lng })
        }
    }
    const view = computeView(pins)

    return (
        <div
            className="bg-white rounded-lg shadow-xl flex flex-col w-[960px] max-w-[95vw] h-[640px] max-h-[90vh] overflow-hidden"
        >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h3 className="text-sm font-medium text-gray-900">
                    Locations on this checkin
                </h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="flex-1 relative">
                {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <Map
                            style={{ width: '100%', height: '100%' }}
                            {...view}
                            mapId={EXPANDED_MAP_ID}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                        >
                            {pins.map((p) => (
                                <AdvancedMarker
                                    key={p.key}
                                    position={{ lat: p.lat, lng: p.lng }}
                                    onClick={() => p.detailValue && onPinSelect(p.detailValue)}
                                >
                                    <LabeledPin label={p.label} />
                                </AdvancedMarker>
                            ))}
                        </Map>
                    </APIProvider>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
                        Map unavailable — VITE_GOOGLE_MAPS_BROWSER_KEY is not set.
                    </div>
                )}
            </div>
        </div>
    )
}

/**
 * AdvancedMarker child: place name floating above a red pin shape. The
 * pin itself is an inline SVG, NOT the `<Pin>` component — when `<Pin>`
 * is wrapped in another element instead of being AdvancedMarker's direct
 * child, its effect runs `removeChild` on the marker content's children
 * to swap in Google's native pin DOM. That nukes our wrapper (taking the
 * label and the React click target with it). Custom HTML keeps everything
 * intact and clickable.
 *
 * The shape is a teardrop with a white inner glyph to mirror Google's
 * default Pin look (red fill, dark-red stroke, white circle).
 */
const LabeledPin: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex flex-col items-center">
        <div className="bg-white px-2 py-0.5 rounded border border-gray-300 shadow-sm text-xs font-medium text-gray-900 whitespace-nowrap max-w-[200px] truncate mb-1">
            {label}
        </div>
        <svg
            viewBox="0 0 24 36"
            width="22"
            height="33"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
        >
            <path
                d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"
                fill={PIN_COLORS.bg}
                stroke={PIN_COLORS.border}
                strokeWidth="1"
            />
            <circle cx="12" cy="12" r="4.5" fill={PIN_COLORS.glyph} />
        </svg>
    </div>
)

export default CheckinExpandedMapModal
